const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

const GOOGLE_ADS_CLICK_KEYS = ["gclid", "gbraid", "wbraid"] as const;

/** OpenAI Ads click reference. Must be forwarded marketing → app for cross-domain attribution. */
const OPENAI_ADS_CLICK_KEYS = ["oppref"] as const;
const ATTRIBUTION_STORAGE_KEY = "rivetai:marketing-attribution:v1";

export const MARKETING_ATTRIBUTION_PARAM_KEYS = [
  ...UTM_KEYS,
  ...GOOGLE_ADS_CLICK_KEYS,
  ...OPENAI_ADS_CLICK_KEYS,
] as const;

export type MarketingAttributionParams = Partial<
  Record<(typeof MARKETING_ATTRIBUTION_PARAM_KEYS)[number], string>
>;

/** @deprecated Prefer MarketingAttributionParams */
export type MarketingUtmParams = MarketingAttributionParams;

type SearchParamsReader = Pick<URLSearchParams, "get">;

export function readMarketingAttributionParams(
  searchParams: SearchParamsReader,
): MarketingAttributionParams {
  const params: MarketingAttributionParams = {};

  for (const key of MARKETING_ATTRIBUTION_PARAM_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      params[key] = value;
    }
  }

  return params;
}

export function readMarketingUtmParams(
  searchParams: SearchParamsReader,
): MarketingUtmParams {
  return readMarketingAttributionParams(searchParams);
}

export function readPersistedMarketingAttributionParams(): MarketingAttributionParams {
  if (typeof window === "undefined") return {};
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? "{}",
    ) as MarketingAttributionParams | undefined;
    if (!parsed || typeof parsed !== "object") return {};
    return Object.fromEntries(
      MARKETING_ATTRIBUTION_PARAM_KEYS.flatMap((key) =>
        typeof parsed[key] === "string" && parsed[key]
          ? [[key, parsed[key]]]
          : [],
      ),
    ) as MarketingAttributionParams;
  } catch {
    return {};
  }
}

export function persistMarketingAttributionParams(
  params: MarketingAttributionParams,
): void {
  if (typeof window === "undefined" || Object.keys(params).length === 0) return;
  try {
    window.localStorage.setItem(
      ATTRIBUTION_STORAGE_KEY,
      JSON.stringify({
        ...readPersistedMarketingAttributionParams(),
        ...params,
      }),
    );
  } catch {
    // Attribution must never block navigation when storage is unavailable.
  }
}

export function resolveMarketingAttributionParams(
  searchParams: SearchParamsReader,
): MarketingAttributionParams {
  return {
    ...readPersistedMarketingAttributionParams(),
    ...readMarketingAttributionParams(searchParams),
  };
}
